import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  Loader2,
  FileText,
  User,
  Phone,
  MapPin,
  Stethoscope,
  Barcode,
  Calendar,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Upload,
  Ban,
  UploadCloud,
  RefreshCw,
  FileCheck,
  DollarSign,
} from "lucide-react";
import toast from "react-hot-toast";
import { OfflineLabtestService } from "../../../../../services/api/OfflineLabtest/offline-labtest.service";
import { OfflineLabtestOrderDetails } from "../../../../../services/api/OfflineLabtest/types";
import { CancelConfirmationModal } from "./CancelConfirmationModal";

interface OfflineLabtestDetailsScreenProps {
  orderId: number;
  onBack: () => void;
  onRefreshListing?: () => void;
}

export const OfflineLabtestDetailsScreen: React.FC<OfflineLabtestDetailsScreenProps> = ({
  orderId,
  onBack,
  onRefreshListing,
}) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<OfflineLabtestOrderDetails | null>(null);

  // Right Sidebar Action States
  // 1. Collect Sample State
  const [sampleCode, setSampleCode] = useState("");
  const [collectingSample, setCollectingSample] = useState(false);

  // 2. Upload Report State
  const [reportFiles, setReportFiles] = useState<File[]>([]);
  const [uploadingReport, setUploadingReport] = useState(false);

  // 3. Cancel Order State
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await OfflineLabtestService.getDetails(orderId);
      if (res.success && res.data) {
        setDetails(res.data);
      } else {
        toast.error(res.message || "Failed to load order details");
      }
    } catch (error: any) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [orderId]);

  // Action Handler 1: Collect Sample
  const handleCollectSampleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sampleCode.trim()) {
      toast.error("Please enter sample barcode / code");
      return;
    }

    try {
      setCollectingSample(true);
      const res = await OfflineLabtestService.collectSample(orderId, sampleCode.trim());
      if (res.success) {
        toast.success(res.message || "Sample collected successfully");
        setSampleCode("");
        fetchDetails();
        if (onRefreshListing) onRefreshListing();
      } else {
        toast.error(res.message || "Failed to record sample collection");
      }
    } catch (error: any) {
      console.error("Collect sample error:", error);
      toast.error(error?.message || "Failed to collect sample");
    } finally {
      setCollectingSample(false);
    }
  };

  // Action Handler 2: Upload Report
  const handleUploadReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reportFiles.length === 0) {
      toast.error("Please select at least one report file");
      return;
    }

    const firstTestId = details?.labtests?.[0]?.id || 0;

    try {
      setUploadingReport(true);
      const res = await OfflineLabtestService.uploadReport(
        orderId,
        firstTestId,
        reportFiles.length === 1 ? reportFiles[0] : reportFiles
      );
      if (res.success) {
        toast.success(res.message || "Reports uploaded successfully");
        setReportFiles([]);
        fetchDetails();
        if (onRefreshListing) onRefreshListing();
      } else {
        toast.error(res.message || "Failed to upload reports");
      }
    } catch (error: any) {
      console.error("Upload report error:", error);
      toast.error(error?.message || "Failed to upload reports");
    } finally {
      setUploadingReport(false);
    }
  };

  // Action Handler 3: Cancel Order Confirm
  const handleConfirmCancelOrder = async () => {
    try {
      setCancelling(true);
      const res = await OfflineLabtestService.cancelOrder(orderId);
      if (res.success) {
        toast.success(res.message || "Order cancelled successfully");
        setShowCancelModal(false);
        fetchDetails();
        if (onRefreshListing) onRefreshListing();
      } else {
        toast.error(res.message || "Failed to cancel order");
      }
    } catch (error: any) {
      console.error("Cancel order error:", error);
      toast.error(error?.message || "Failed to cancel order");
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "booked":
        return <span className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-semibold uppercase tracking-wider">Booked</span>;
      case "sample_collected":
        return <span className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-semibold uppercase tracking-wider">Sample Collected</span>;
      case "report_generated":
        return <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold uppercase tracking-wider">Report Generated</span>;
      case "cancelled":
        return <span className="px-3 py-1 bg-rose-50 text-rose-700 border border-rose-200 rounded-full text-xs font-semibold uppercase tracking-wider">Cancelled</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">{status}</span>;
    }
  };

  return (
    <div className="w-full flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-200/80 overflow-hidden">
      {/* Top Navigation Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/80 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200/60 rounded-xl transition-colors flex items-center gap-1.5 text-xs font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <div className="h-5 w-px bg-gray-300" />
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-gray-900">
              Offline Labtest Order #{details?.invoiceNumber || orderId}
            </h2>
            {details && getStatusBadge(details.status)}
          </div>
        </div>

        <button
          onClick={fetchDetails}
          title="Refresh Order Details"
          className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors border border-gray-200"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-emerald-600" : ""}`} />
        </button>
      </div>

      {/* Screen Body */}
      {loading ? (
        <div className="py-24 flex flex-col items-center justify-center text-gray-400 gap-2 flex-1">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-xs">Fetching order details...</span>
        </div>
      ) : !details ? (
        <div className="py-20 text-center text-gray-400 flex-1">
          <AlertCircle className="w-10 h-10 mx-auto text-rose-500 mb-2" />
          <p className="text-sm font-medium text-gray-700">Failed to load details for Order #{orderId}</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-semibold"
          >
            Go Back
          </button>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-6 flex flex-col lg:flex-row gap-6">
          {/* LEFT COLUMN (70%): Order Information, Labtests Table, Documents & Invoice */}
          <div className="flex-1 space-y-6">
            {/* Info Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Patient Info Card */}
              <div className="p-4 bg-gray-50/80 rounded-xl border border-gray-200 space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-emerald-600" /> Patient Details
                </h4>
                <div className="text-sm font-bold text-gray-900">{details.patientName || "N/A"}</div>
                <div className="text-xs text-gray-600 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-gray-400" /> {details.patientMobileNumber}
                </div>
                <div className="text-xs text-gray-600 flex items-start gap-1.5 pt-2 border-t border-gray-200/60">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                  <span>
                    {details.address} {details.pincode ? ` - ${details.pincode}` : ""}
                  </span>
                </div>
              </div>

              {/* Doctor & Sample Collection Info Card */}
              <div className="p-4 bg-gray-50/80 rounded-xl border border-gray-200 space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                  <Stethoscope className="w-3.5 h-3.5 text-emerald-600" /> Doctor & Sample Info
                </h4>
                <div className="text-xs text-gray-600 flex items-center gap-2">
                  <span className="text-gray-500">Attending Doctor:</span>
                  <span className="font-bold text-gray-900">{details.doctorName || "N/A"}</span>
                </div>
                <div className="text-xs text-gray-600 flex items-center gap-2">
                  <Barcode className="w-3.5 h-3.5 text-gray-400" />
                  <span>Sample Barcode:</span>
                  <span className="font-mono bg-white px-2.5 py-0.5 rounded border border-gray-300 text-gray-800 font-bold">
                    {details.collectedSampleCode || "Not Collected"}
                  </span>
                </div>
                {details.sampleCollectionDate && (
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span>Collected: {new Date(details.sampleCollectionDate).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Ordered Lab Tests Table */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <span>Ordered Lab Tests</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                    {details.labtests?.length || 0}
                  </span>
                </h3>
              </div>

              <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm">
                <table className="w-full text-left text-xs text-gray-600">
                  <thead className="bg-gray-50 border-b border-gray-200 font-semibold text-gray-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3">#</th>
                      <th className="px-4 py-3">Test Name</th>
                      <th className="px-4 py-3">Booked For</th>
                      <th className="px-4 py-3 text-right">MRP (₹)</th>
                      <th className="px-4 py-3 text-right">Discount (₹)</th>
                      <th className="px-4 py-3 text-right">Final Price (₹)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {details.labtests && details.labtests.length > 0 ? (
                      details.labtests.map((test, index) => (
                        <tr key={test.id || index} className="hover:bg-gray-50/60">
                          <td className="px-4 py-3 text-gray-400 font-mono">{index + 1}</td>
                          <td className="px-4 py-3 font-bold text-gray-900">{test.labTestName}</td>
                          <td className="px-4 py-3 font-semibold text-gray-800">
                            {(test as any).patientName || (test as any).bookedPatientName || details.patientName || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-400">
                            ₹{Number(test.mrp).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right text-amber-600 font-medium">
                            ₹{Number(test.discount).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-emerald-700 text-sm">
                            ₹{Number(test.price).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                          No lab tests listed
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* DOCUMENTS & INVOICE CARD (MOVED TO LEFT SIDE BELOW LAB TESTS) */}
            <div className="p-5 bg-white border border-gray-200 rounded-xl shadow-sm space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5 border-b border-gray-100 pb-2">
                <FileCheck className="w-4 h-4 text-emerald-600" /> Documents & Invoice
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Invoice Bill PDF Button */}
                {details.billUrl ? (
                  <a
                    href={details.billUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-3 bg-emerald-50/60 border border-emerald-200 rounded-xl hover:bg-emerald-100/60 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2.5">
                      <FileText className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="text-xs font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                          Invoice Bill PDF
                        </p>
                        <p className="text-[10px] text-gray-500">Official Invoice Download</p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-emerald-600" />
                  </a>
                ) : (
                  <div className="p-3 bg-gray-50 border border-dashed border-gray-200 rounded-xl text-xs text-gray-400 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>Bill PDF generating in background...</span>
                  </div>
                )}
              </div>

              {/* Uploaded Test Reports List */}
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-700">Uploaded Lab Reports ({details.reports?.length || 0})</p>
                {details.reports && details.reports.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {details.reports.map((report, idx) => (
                      <a
                        key={report.id || idx}
                        href={report.path}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-white transition-all flex items-center justify-between text-xs group"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span className="font-semibold text-gray-800 truncate">Test Report #{idx + 1}</span>
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-600 shrink-0" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-400 italic">No reports uploaded yet</p>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN SIDEBAR (30%): Payment Summary & Action Panels */}
          <div className="w-full lg:w-80 shrink-0 space-y-5">
            {/* PAYMENT SUMMARY CARD (MOVED TO RIGHT SIDEBAR ABOVE ACTIONS) */}
            <div className="p-5 bg-emerald-50/50 rounded-xl border border-emerald-200 space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-800 border-b border-emerald-200/80 pb-2 flex items-center gap-1.5">
                <DollarSign className="w-4 h-4 text-emerald-600" /> Payment & Summary
              </h4>

              <div className="space-y-2 text-xs text-gray-700">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Payment Type:</span>
                  <span className="font-bold text-gray-900 uppercase bg-white px-2 py-0.5 rounded border border-emerald-200">
                    {details.paymentType}
                  </span>
                </div>

                <div className="flex justify-between items-center text-gray-600">
                  <span>Cash Paid:</span>
                  <span className="font-semibold text-gray-900">₹{Number(details.cashAmount || 0).toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center text-gray-600">
                  <span>Online Paid:</span>
                  <span className="font-semibold text-gray-900">₹{Number(details.onlineAmount || 0).toFixed(2)}</span>
                </div>

                <div className="pt-2 border-t border-emerald-200/60 flex justify-between items-center text-gray-600">
                  <span>Subtotal (MRP):</span>
                  <span className="font-semibold">₹{Number(details.subtotal).toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center text-amber-700">
                  <span>Total Discount:</span>
                  <span className="font-semibold">- ₹{Number(details.discount).toFixed(2)}</span>
                </div>

                <div className="pt-2 border-t border-emerald-300 flex justify-between items-center text-sm font-bold text-emerald-900">
                  <span>Total Amount:</span>
                  <span>₹{Number(details.totalAmount).toFixed(2)}</span>
                </div>

                {details.remarks && (
                  <div className="pt-2 border-t border-emerald-200/60 text-[11px]">
                    <span className="text-gray-500 font-semibold block">Remarks:</span>
                    <span className="italic text-gray-700">"{details.remarks}"</span>
                  </div>
                )}
              </div>
            </div>

            {/* ACTION PANEL (BOOKED: Collect Sample & Cancel) */}
            {details.status === "booked" && (
              <div className="p-4 bg-white border border-blue-200 rounded-xl shadow-sm space-y-4">
                <div className="flex items-center gap-2 text-blue-900 font-bold text-xs uppercase tracking-wider border-b border-blue-100 pb-2">
                  <Barcode className="w-4 h-4 text-blue-600" />
                  <span>Sample Collection Action</span>
                </div>

                <form onSubmit={handleCollectSampleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Sample Barcode / Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. SMP-BARCODE-99482"
                      value={sampleCode}
                      onChange={(e) => setSampleCode(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={collectingSample || !sampleCode.trim()}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow disabled:opacity-50 transition-all"
                  >
                    {collectingSample && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span>Submit & Collect Sample</span>
                  </button>
                </form>

                <div className="pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setShowCancelModal(true)}
                    disabled={cancelling}
                    className="w-full py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-lg flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                  >
                    {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                    <span>Cancel Order</span>
                  </button>
                </div>
              </div>
            )}

            {/* ACTION PANEL (SAMPLE COLLECTED: Upload Test Report) */}
            {details.status === "sample_collected" && (
              <div className="p-4 bg-white border border-emerald-200 rounded-xl shadow-sm space-y-4">
                <div className="flex items-center gap-2 text-emerald-900 font-bold text-xs uppercase tracking-wider border-b border-emerald-100 pb-2">
                  <UploadCloud className="w-4 h-4 text-emerald-600" />
                  <span>Upload Test Reports</span>
                </div>

                <form onSubmit={handleUploadReportSubmit} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Select Report Files (PDF/Images) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      required
                      accept="application/pdf,image/*"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) setReportFiles(Array.from(e.target.files));
                      }}
                      className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer"
                    />
                  </div>

                  {reportFiles.length > 0 && (
                    <div className="text-[10px] text-gray-600 bg-gray-50 p-2 rounded border border-gray-200 truncate">
                      Selected ({reportFiles.length}): {reportFiles.map((f) => f.name).join(", ")}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={uploadingReport || reportFiles.length === 0}
                    className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow disabled:opacity-50 transition-all"
                  >
                    {uploadingReport && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span>Upload Reports & Complete</span>
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancel Order Confirmation Modal */}
      {details && (
        <CancelConfirmationModal
          isOpen={showCancelModal}
          orderNumber={details.invoiceNumber || details.orderId}
          loading={cancelling}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleConfirmCancelOrder}
        />
      )}
    </div>
  );
};
